import { Header } from '../components/layout/Header';
import { SEO } from '../components/common/SEO';

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Terms of Service – WaitlistPoker" path="/terms" />
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing or using WaitlistPoker.com ("the Service"), operated by PingSolve LLC. ("Company", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Age Requirement:</strong> You must be at least 21 years old to use this Service. By using the Service, you represent and warrant that you are at least 21 years of age.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                WaitlistPoker.com is a digital platform that facilitates poker room management and waitlist services. The Service allows users to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>View available poker tables and room information</li>
                <li>Join waitlists for poker games</li>
                <li>Receive notifications about table availability</li>
                <li>Access poker room analytics and metrics</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> We do not operate poker rooms or gambling facilities. We are a technology platform that connects users with third-party poker rooms. All poker games and gambling activities are conducted by independent third-party establishments.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts and Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                To use certain features of the Service, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Providing accurate and complete information during registration</li>
                <li>Maintaining the security of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Complying with all applicable laws and regulations</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">Prohibited Usernames and Display Names</h3>
              <p className="text-gray-700 mb-4">
                Users shall not create, use, or display any username, screen name, poker alias, or other identifier that PingSolve LLC, in its sole discretion, deems to be offensive, obscene, defamatory, harassing, discriminatory, infringing, unlawful, or otherwise harmful. PingSolve LLC reserves the right to review, monitor, remove, suspend, or terminate any account or access to its services found to be in violation of this provision and to pursue any and all legal remedies available under applicable law.
              </p>
              <p className="text-gray-700 mb-4">
                PingSolve LLC shall not be held liable or responsible for any damages, losses, liabilities, or injuries arising from or relating to a user's creation, use, or public display of any prohibited name or identifier, including, without limitation, any appearance of such identifiers on poker room dashboards or related service interfaces. Users acknowledge and agree that they are solely responsible for any consequences arising from their chosen identifiers and any resulting harm to others.
              </p>
              <p className="text-gray-700 mb-4">
                By accessing or using the services provided by PingSolve LLC, users acknowledge and understand that they may be exposed to content, usernames, or identifiers created by other users that may be offensive, inappropriate, or otherwise objectionable. Users agree that PingSolve LLC shall not be held liable in any manner for the presence, display, or continued accessibility of such content and that their sole remedy shall be to discontinue use of the service or report the offending content through the designated reporting mechanisms provided by PingSolve LLC.
                </p>
              <p className="text-gray-700 mb-4"> 
                Poker rooms, tournament organizers, and other entities that subscribe to, integrate with, or publicly display any dashboard, leaderboard, or similar output generated by PingSolve LLC’s services do so at their own discretion and assume full responsibility for any risks associated with the public display of user-generated identifiers. Such entities acknowledge and agree that PingSolve LLC shall not be held liable for any offensive, inappropriate, or unlawful content displayed through their public interfaces, and that they remain solely responsible for moderating, managing, and monitoring any such content that appears as part of their publicly accessible platforms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Future Functionality and Communications</h2>
              <p className="text-gray-700 mb-4">
                By using the Service, you consent to receive communications from us, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>SMS text messages regarding waitlist updates and table availability</li>
                <li>Push notifications through mobile applications</li>
                <li>Email communications about service updates and features</li>
                <li>Marketing communications about promotions and new features</li>
                <li>Analytics and usage reports</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You may opt out of marketing communications at any time by following the unsubscribe instructions in our messages or by contacting us at support@PingSolveai.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Collection and Usage</h2>
              <p className="text-gray-700 mb-4">
                We collect and use information as described in our Privacy Policy. By using the Service, you grant us the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Collect and store your personal information (name, email, phone number)</li>
                <li>Use your data to provide and improve our services</li>
                <li>Generate aggregate analytics about poker room metrics and usage patterns</li>
                <li>Share anonymized, aggregated data with poker room partners</li>
                <li>Use cookies and similar technologies to enhance your experience</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Prohibited Uses</h2>
              <p className="text-gray-700 mb-4">You may not use the Service to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Engage in fraudulent or deceptive practices</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Use the Service for any commercial purpose without our written consent</li>
                <li>Create multiple accounts to circumvent restrictions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Disclaimers and Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                <strong>No Gambling Services:</strong> We do not provide gambling services, operate poker rooms, or handle money transactions related to poker games. All gambling activities are conducted by third-party establishments.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Service Availability:</strong> The Service is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Limitation of Liability:</strong> To the maximum extent permitted by law, PingSolve shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Account Termination</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Violation of these Terms</li>
                <li>Fraudulent or suspicious activity</li>
                <li>Extended periods of inactivity</li>
                <li>At our sole discretion</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You may terminate your account at any time by contacting us at support@PingSolveai.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-gray-700 mb-4">
                Email: Support@pingsolveai.com<br />
                Company: PingSolve LLC.<br />
                Service: WaitlistPoker.com
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
